import { useEffect, useRef, useState } from "react";
import { STORY_CHAPTER_EDGES } from "./home-story-layout";
import type { HomeStoryScene } from "./home-story-scene";
import type { HomeStoryShowcase } from "./home-story-showcase";

export type HomeStoryStatus = "loading" | "ready" | "error";

function chapterFor(progress: number) {
  return STORY_CHAPTER_EDGES.filter((edge) => progress >= edge).length;
}

/**
 * 叙事可滚动的距离：区段总高减去两屏。
 * 最后一屏留给转场，下一屏以卡片形式盖上来，期间章节进度保持在终点。
 */
export function storyScrollDistance(sectionHeight: number, viewportHeight: number) {
  return Math.max(sectionHeight - viewportHeight * 2, 1);
}

function supportsWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function readBrandColor() {
  const value = getComputedStyle(document.documentElement).getPropertyValue("--brand").trim();
  return value || "#2ee6d6";
}

/**
 * 首页滚动叙事：把置顶区段的滚动进度同步给 CSS 变量 `--story` 与 3D 场景，
 * 并返回当前章节，供文字层切换。
 */
export function useHomeStoryScene(showcase: HomeStoryShowcase | null) {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HomeStoryScene | null>(null);
  const progressRef = useRef(0);
  const showcaseRef = useRef(showcase);
  showcaseRef.current = showcase;
  const [chapter, setChapter] = useState(0);
  const [status, setStatus] = useState<HomeStoryStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const distance = storyScrollDistance(rect.height, window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / distance));
      const exit = Math.min(1, Math.max(0, (-rect.top - distance) / window.innerHeight));
      section.style.setProperty("--story-exit", exit.toFixed(4));
      progressRef.current = progress;
      section.style.setProperty("--story", progress.toFixed(4));
      sceneRef.current?.setProgress(progress);
      setChapter(chapterFor(progress));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    const section = sectionRef.current;
    if (!stage || !section) return;
    if (!supportsWebGL()) {
      setStatus("error");
      return;
    }
    let disposed = false;
    let visible = true;
    let cleanup = () => {};
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setStatus("loading");

    import("./home-story-scene")
      .then(({ createHomeStoryScene }) => {
        if (disposed) return;
        const scene = createHomeStoryScene(stage, { reducedMotion, brandColor: readBrandColor() });
        sceneRef.current = scene;
        scene.setProgress(progressRef.current);
        if (showcaseRef.current) scene.setShowcase(showcaseRef.current);
        const sync = () => {
          if (visible && !document.hidden) scene.start();
          else scene.stop();
        };
        const observer = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          sync();
        });
        observer.observe(section);
        const onPointer = (event: PointerEvent) => {
          if (event.pointerType !== "mouse") return;
          scene.setPointer((event.clientX / window.innerWidth) * 2 - 1, -((event.clientY / window.innerHeight) * 2 - 1));
        };
        window.addEventListener("pointermove", onPointer, { passive: true });
        document.addEventListener("visibilitychange", sync);
        sync();
        setStatus("ready");
        cleanup = () => {
          observer.disconnect();
          window.removeEventListener("pointermove", onPointer);
          document.removeEventListener("visibilitychange", sync);
          scene.dispose();
          sceneRef.current = null;
        };
      })
      .catch(() => {
        if (!disposed) setStatus("error");
      });

    return () => {
      disposed = true;
      cleanup();
    };
  }, [attempt]);

  useEffect(() => {
    if (showcase) sceneRef.current?.setShowcase(showcase);
  }, [showcase]);

  return { sectionRef, stageRef, chapter, status, retry: () => setAttempt((value) => value + 1) };
}
